import { useEventListener } from '@vueuse/core';
import { isHtmlElement } from '@/js/helpers/type-guards/isHtmlElement';
import { KeyEnum } from '@/js/enums/enums';
import { isGivenHtmlType } from '@/js/helpers/type-guards/isGivenHtmlType';
import { ModalTypesEnum } from './enums';

export interface ModalConfig {
    modalId: string;
    triggerSelector: string;
    modalContentOverflow?: string;
}

interface ModalTriggerData {
    type?: ModalTypesEnum;
    videoSrc?: string;
    width?: string;
    height?: string;
    background?: string;
    borderRadius?: string;
    closeIconColor?: string;
    mobileFullscreen?: boolean;
}

export class GeneralModal {
    private readonly modalId: string;

    private readonly triggerSelector: string;

    private readonly modalLoaderId: string = 'modal-loader';

    private readonly modalContentOverflow: string;

    private static styleElement: HTMLStyleElement | null = null;

    constructor(config: ModalConfig) {
        this.modalId = config.modalId;
        this.triggerSelector = config.triggerSelector;
        this.modalContentOverflow = config.modalContentOverflow || 'scroll';
    }

    private static addClassesToDOM(): void {
        if (GeneralModal.styleElement) return;

        GeneralModal.styleElement = document.createElement('style');
        GeneralModal.styleElement.innerHTML = `
          .no-scroll {
            overflow: hidden;
          }

          .display-none {
            display: none !important;
          }
        `;

        document.head.appendChild(GeneralModal.styleElement);
    }

    private static toggleScrollability(): void {
        document.body.classList.toggle('no-scroll');
    }

    private setupModal(modalTrigger: HTMLDivElement): void {
        const modal = document.getElementById(this.modalId);
        if (!modal) return;

        const modalContent: HTMLDivElement = modal.querySelector('#modal-content');
        const closeButton: HTMLElement = modal.querySelector('.modal-component__close');
        if (!modalContent || !closeButton) return;

        const modalData = modalTrigger.dataset as ModalTriggerData;
        let removeKeydownListener: (() => void) | undefined;
        let removeBackdropListener: (() => void) | undefined;
        let removeCloseButtonListener: (() => void) | undefined;

        const cleanUpEvents = () => {
            removeKeydownListener?.();
            removeBackdropListener?.();
            removeCloseButtonListener?.();
        };

        const cleanupContent = () => {
            if (modalData.type === ModalTypesEnum.VIDEO) {
                const modalLoader = modalContent.querySelector(`#${this.modalLoaderId}`);
                if (modalLoader) {
                    modalContent.removeChild(modalLoader);
                }
                if (modalContent.firstChild) {
                    modalContent.removeChild(modalContent.firstChild);
                }
            }
        };

        const closeModal = () => {
            modal.classList.remove('show');
            GeneralModal.toggleScrollability();
            cleanUpEvents();
            cleanupContent();
        };

        const closeModalOnBackdropClick = (eventTarget: EventTarget) => {
            if (!isHtmlElement(eventTarget)) return;
            if (eventTarget.id === this.modalId) {
                closeModal();
            }
        };

        const handleEscapeKeydown = (pressedKey: KeyboardEvent['key']) => {
            if (pressedKey === KeyEnum.ESCAPE || pressedKey === KeyEnum.ESC) {
                closeModal();
            }
        };

        const applyModalStyles = () => {
            const {
                width,
                height,
                background,
                borderRadius,
                closeIconColor,
                mobileFullscreen,
            } = modalData;

            Object.assign(modalContent.style, {
                width,
                height,
                background,
                borderRadius,
                overflow: mobileFullscreen ? this.modalContentOverflow : undefined,
            });

            closeButton.style.color = closeIconColor;

            if (mobileFullscreen) {
                modalContent.classList.add('modal-component__content--fullscreen');
            }

            modal.style.display = 'flex';
            requestAnimationFrame(() => {
                modal.classList.add('show');
            });
        };

        const setModalContent = () => {
            if (!modalData.type) return;

            if (modalData.type === ModalTypesEnum.VIDEO && modalData.videoSrc) {
                const iframe = document.createElement('iframe');
                iframe.classList.add('video-modal__iframe', 'display-none');
                iframe.src = modalData.videoSrc;
                iframe.title = 'YouTube Video Player';
                iframe.allowFullscreen = true;

                const loaderContainer = document.createElement('div');
                loaderContainer.id = this.modalLoaderId;
                loaderContainer.classList.add('modal-component__loader-container');

                const loader = document.createElement('div');
                loader.classList.add('modal-component__loader');
                loaderContainer.appendChild(loader);

                modalContent.insertBefore(loaderContainer, modalContent.firstChild);
                modalContent.insertBefore(iframe, modalContent.firstChild);

                useEventListener(iframe, 'load', () => {
                    modalContent.removeChild(loaderContainer);
                    iframe.classList.remove('display-none');
                });
            }
        };

        const openModal = () => {
            GeneralModal.toggleScrollability();
            setModalContent();
            applyModalStyles();

            removeKeydownListener = useEventListener(
                closeButton,
                'click',
                closeModal,
                { once: true },
            );
            removeBackdropListener = useEventListener(
                modal,
                'click',
                (event) => closeModalOnBackdropClick(event.target),
            );
            removeCloseButtonListener = useEventListener(
                window,
                'keydown',
                (event) => handleEscapeKeydown(event.key),
            );
        };

        openModal();
    }

    private addClickListener(): void {
        useEventListener(document, 'click', (event) => {
            const clickedElement = event.target;
            if (!isGivenHtmlType(clickedElement, HTMLElement)) return;

            const modalTrigger: HTMLDivElement | null = clickedElement.closest(this.triggerSelector);
            if (modalTrigger) {
                this.setupModal(modalTrigger);
            }
        });
    }

    public initialize(): void {
        GeneralModal.addClassesToDOM();
        this.addClickListener();
    }

    public remoteModalOpen(fakeTriggerElement: HTMLDivElement): void {
        this.setupModal(fakeTriggerElement);
    }
}
